import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * useDraftCollaboration Hook (Client Version)
 * Manages draft collaboration operations from client perspective
 */
export function useDraftCollaboration(documentId) {
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  const authHeaders = (isJson = true) => {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (isJson) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  // Fetch document data
  const {
    data: document,
    isLoading,
    error: fetchError,
    refetch,
  } = useQuery({
    queryKey: ['document', documentId],
    queryFn: async () => {
      const response = await fetch(`/api/documents/${documentId}`, {
        headers: authHeaders(false),
      });
      if (!response.ok) throw new Error('Failed to fetch document');
      const data = await response.json();
      return data.document || data;
    },
    enabled: !!documentId,
  });

  // Upload client revision
  const uploadClientRevisionMutation = useMutation({
    mutationFn: async ({ file, clientInfo }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('clientName', clientInfo.name);
      formData.append('clientEmail', clientInfo.email);

      const response = await fetch(`/api/drafts/client/${documentId}/upload-revision`, {
        method: 'POST',
        headers: authHeaders(false),
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to upload revision');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  // Submit client draft with feedback
  const submitClientDraftMutation = useMutation({
    mutationFn: async ({ file, feedback }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('feedback', JSON.stringify(feedback));

      const response = await fetch(`/api/drafts/client/${documentId}/submit-draft`, {
        method: 'POST',
        headers: authHeaders(false),
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to submit draft');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  // Approve draft
  const approveDraftMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/drafts/client/${documentId}/approve`, {
        method: 'POST',
        headers: authHeaders(false),
      });

      if (!response.ok) throw new Error('Failed to approve draft');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  // Request changes
  const requestChangesMutation = useMutation({
    mutationFn: async (reason) => {
      const response = await fetch(`/api/drafts/client/${documentId}/request-changes`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) throw new Error('Failed to request changes');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  // Add comment
  const addCommentMutation = useMutation({
    mutationFn: async ({ iterationNumber, comment }) => {
      const response = await fetch(
        `/api/drafts/${documentId}/iteration/${iterationNumber}/comment`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(comment),
        }
      );

      if (!response.ok) throw new Error('Failed to add comment');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  // Record final signature
  const recordFinalSignatureMutation = useMutation({
    mutationFn: async (signatureData) => {
      const response = await fetch(
        `/api/drafts/client/${documentId}/sign-final-version`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(signatureData),
        }
      );

      if (!response.ok) throw new Error('Failed to record signature');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  // Request final version revision
  const requestFinalVersionRevisionMutation = useMutation({
    mutationFn: async (reason) => {
      const response = await fetch(
        `/api/drafts/client/${documentId}/request-final-revision`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) throw new Error('Failed to request revision');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  const saveContentMutation = useMutation({
    mutationFn: async ({ content, fileName }) => {
      const response = await fetch(`/api/drafts/${documentId}/content`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ content, fileName }),
      });
      if (!response.ok) throw new Error('Failed to save content');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await fetch(`/api/drafts/${documentId}/workflow`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to update workflow');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  const advanceWorkflowMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await fetch(`/api/drafts/${documentId}/workflow/advance`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to advance workflow');
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  const returnWorkflowMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await fetch(`/api/drafts/${documentId}/workflow/return`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to return the document');
      }
      return response.json();
    },
    onSuccess: () => {
      refetch();
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  // Get draft history
  const { data: history } = useQuery({
    queryKey: ['draftHistory', documentId],
    queryFn: async () => {
      const response = await fetch(`/api/drafts/${documentId}/history`, {
        headers: authHeaders(false),
      });
      if (!response.ok) throw new Error('Failed to fetch history');
      return response.json();
    },
    enabled: false,
  });

  return {
    // Data
    document,
    history,
    isLoading,
    error: error || fetchError?.message,

    // Client mutations
    uploadClientRevision: (file, clientInfo) =>
      uploadClientRevisionMutation.mutateAsync({ file, clientInfo }),
    submitClientDraft: (file, feedback) =>
      submitClientDraftMutation.mutateAsync({ file, feedback }),
    approveDraft: () => approveDraftMutation.mutateAsync(),
    requestChanges: (reason) => requestChangesMutation.mutateAsync(reason),
    addComment: (iterationNumber, comment) =>
      addCommentMutation.mutateAsync({ iterationNumber, comment }),
    recordFinalSignature: (signatureData) =>
      recordFinalSignatureMutation.mutateAsync(signatureData),
    requestFinalVersionRevision: (reason) =>
      requestFinalVersionRevisionMutation.mutateAsync(reason),
    saveContent: (content, fileName) =>
      saveContentMutation.mutateAsync({ content, fileName }),
    updateWorkflow: (payload) => updateWorkflowMutation.mutateAsync(payload),
    advanceWorkflow: (payload) => advanceWorkflowMutation.mutateAsync(payload),
    returnWorkflow: (payload) => returnWorkflowMutation.mutateAsync(payload),

    // Utilities
    getDraftHistory: async () => {
      return new Promise((resolve, reject) => {
        fetch(`/api/drafts/${documentId}/history`, {
          headers: authHeaders(false),
        })
          .then((res) => {
            if (!res.ok) throw new Error('Failed to fetch history');
            return res.json();
          })
          .then(resolve)
          .catch(reject);
      });
    },
  };
}
